import { useConfirm } from "@/contexts/ConfirmDialogContext";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@a7y/tailwind-catalyst";
import { TrashIcon } from "@heroicons/react/16/solid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProductEmailSend } from "./types";

interface DraftsTabProps {
  onLoadDraft: (draft: ProductEmailSend) => void;
}

const MODE_LABELS: Record<string, string> = {
  plaintext: "Plain Text",
  html: "HTML",
  markdown: "Markdown",
};

export function DraftsTab({ onLoadDraft }: DraftsTabProps) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const { data, isLoading } = useQuery({
    queryKey: ["product-email-drafts"],
    queryFn: () => api.listDrafts(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteDraft(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["product-email-drafts"] }),
  });

  const drafts: ProductEmailSend[] = data?.drafts ?? [];

  return (
    <Table dense striped>
      <TableHead>
        <TableRow>
          <TableHeader>Subject</TableHeader>
          <TableHeader>Input Mode</TableHeader>
          <TableHeader>Last Updated</TableHeader>
          <TableHeader className="text-right">Actions</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={4} className="text-center">
              <div className="mx-auto h-6 w-6 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600" />
            </TableCell>
          </TableRow>
        ) : drafts.length === 0 ? (
          <TableRow>
            <TableCell colSpan={4} className="py-8 text-center">
              <p className="text-zinc-500">No drafts saved</p>
            </TableCell>
          </TableRow>
        ) : (
          drafts.map((draft) => (
            <TableRow
              key={draft.id}
              className="cursor-pointer"
              onClick={() => onLoadDraft(draft)}
            >
              <TableCell className="font-medium">
                {draft.subject || "(No subject)"}
              </TableCell>
              <TableCell>
                {MODE_LABELS[draft.inputMode] ?? draft.inputMode}
              </TableCell>
              <TableCell className="text-zinc-500">
                {formatDate(draft.updatedAt)}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  plain
                  onClick={async (e: React.MouseEvent) => {
                    e.stopPropagation();
                    const ok = await confirm({
                      title: `Delete draft "${draft.subject || "(No subject)"}"?`,
                      confirmLabel: "Delete",
                      confirmColor: "red",
                    });
                    if (ok) deleteMutation.mutate(draft.id);
                  }}
                  title="Delete draft"
                >
                  <TrashIcon className="text-red-500" />
                </Button>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
